<script lang="ts">
  import { createEventDispatcher, onMount, onDestroy } from 'svelte';
  import { getVoterHistory, deleteContact, humanizeError, type ContactHistoryItem } from '$lib/api';
  import type { VoterSummary } from '$lib/stores/voters';
  import { currentUser } from '$lib/stores/auth';

  export let voter: Pick<VoterSummary, 'id' | 'first_name' | 'last_name'>;

  const dispatch = createEventDispatcher<{ close: void; deleted: void }>();

  // Exported so tests can inject rows directly: onMount does not fire in the
  // jsdom test env, the same reason WalkList.svelte exports items + loading.
  export let items: ContactHistoryItem[] = [];
  export let loading = true;
  let error: string | null = null;
  let deletingId: string | null = null;
  let alive = true;
  onDestroy(() => { alive = false; });

  function canDelete(_c: ContactHistoryItem): boolean {
    // Backend now 403s volunteers even for their own contacts — only admins
    // may delete. Gate the UI to match.
    return $currentUser?.role === 'admin';
  }

  async function handleDelete(c: ContactHistoryItem) {
    const who = c.user.display_name ?? c.user.username;
    if (!confirm(`Delete this contact by ${who}? This can't be undone.`)) return;
    deletingId = c.id;
    try {
      await deleteContact(c.id);
      if (!alive) return;
      items = items.filter(i => i.id !== c.id);
      dispatch('deleted');  // parent should refresh the voter's pin
    } catch (e: any) {
      if (!alive) return;
      error = humanizeError(e, 'Could not delete — please try again.');
    } finally {
      if (alive) deletingId = null;
    }
  }

  type FieldKey =
    | 'date'
    | 'volunteer'
    | 'method'
    | 'result'
    | 'support'
    | 'note'
    | 'issues'
    | 'yard_sign'
    | 'volunteer_interest'
    | 'donation_interest'
    | 'callback_at';

  const FIELD_LABELS: Record<FieldKey, string> = {
    date: 'Date',
    volunteer: 'Volunteer',
    method: 'Method',
    result: 'Result',
    support: 'Support level',
    note: 'Note',
    issues: 'Issues',
    yard_sign: 'Yard sign',
    volunteer_interest: 'Vol. interest',
    donation_interest: 'Donation interest',
    callback_at: 'Callback at',
  };

  // Default-visible fields: the high-signal ones.
  const DEFAULT_VISIBLE: Record<FieldKey, boolean> = {
    date: true,
    volunteer: true,
    method: true,
    result: true,
    support: true,
    note: true,
    issues: true,
    yard_sign: false,
    volunteer_interest: false,
    donation_interest: false,
    callback_at: false,
  };

  let visible: Record<FieldKey, boolean> = { ...DEFAULT_VISIBLE };
  let showPicker = false;
  const FIELD_KEYS = Object.keys(FIELD_LABELS) as FieldKey[];

  onMount(async () => {
    try {
      items = await getVoterHistory(voter.id);
    } catch (e: any) {
      error = humanizeError(e, 'Could not load history — please try again.');
    } finally {
      loading = false;
    }
  });

  function fmtDate(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  }

  const METHOD_LABELS: Record<string, string> = {
    door: 'Door', phone: 'Phone', text: 'Text', mail: 'Mail', event: 'Event',
  };
  const RESULT_LABELS: Record<string, string> = {
    spoke: 'Spoke',
    not_home: 'Not home',
    refused: 'Refused',
    moved: 'Moved',
    wrong_address: 'Wrong address',
    no_response: 'No response',
    wrong_number: 'Wrong number',
    sent: 'Sent',
    returned_undeliverable: 'Returned undeliverable',
    signed_petition: 'Signed petition',
  };

  function fmtBool(v: boolean | null): string {
    if (v === null) return '—';
    return v ? 'Yes' : 'No';
  }
</script>

<div class="overlay" role="dialog" aria-modal="true" aria-label="Contact history">
  <div class="sheet">
    <header class="header">
      <h2>History — {voter.first_name} {voter.last_name}</h2>
      <div class="header-actions">
        <button
          class="picker-toggle"
          on:click={() => (showPicker = !showPicker)}
          aria-expanded={showPicker}
          aria-label="Choose visible fields"
          title="Choose fields"
        >Fields ⚙</button>
        <button class="close-btn" on:click={() => dispatch('close')} aria-label="Close">✕</button>
      </div>
    </header>

    {#if showPicker}
      <fieldset class="picker">
        <legend>Show columns</legend>
        {#each FIELD_KEYS as key (key)}
          <label class="picker-row">
            <input type="checkbox" bind:checked={visible[key]} />
            {FIELD_LABELS[key]}
          </label>
        {/each}
      </fieldset>
    {/if}

    <div class="body">
      {#if loading}
        <p class="muted">Loading…</p>
      {:else if error}
        <p class="error" role="alert">{error}</p>
      {:else if items.length === 0}
        <p class="muted">No contacts logged for this voter yet.</p>
      {:else}
        <ul class="history-list">
          {#each items as c (c.id)}
            <li class="entry">
              <div class="entry-head">
                {#if visible.date}
                  <span class="entry-date">{fmtDate(c.created_at)}</span>
                {/if}
                {#if visible.volunteer}
                  <span class="entry-vol">{c.user.display_name ?? c.user.username}</span>
                {/if}
                {#if visible.result}
                  <span class="badge result-{c.contact_result}">{RESULT_LABELS[c.contact_result] ?? c.contact_result}</span>
                {/if}
                {#if visible.method}
                  <span class="badge">{METHOD_LABELS[c.contact_method] ?? c.contact_method}</span>
                {/if}
                {#if canDelete(c)}
                  <button
                    class="delete-btn"
                    on:click={() => handleDelete(c)}
                    disabled={deletingId === c.id}
                    aria-label="Delete this contact"
                    title="Delete this contact"
                  >{deletingId === c.id ? '…' : '✕'}</button>
                {/if}
                {#if visible.support && c.support_level !== null}
                  <span class="badge support-{c.support_level}">Support {c.support_level}/5</span>
                {/if}
                {#if c.donated}
                  <span class="badge outcome-badge">Donated</span>
                {/if}
                {#if c.volunteered}
                  <span class="badge outcome-badge">Signed up to volunteer</span>
                {/if}
              </div>

              {#if visible.note && c.note}
                <p class="entry-note">{c.note}</p>
              {/if}

              {#if visible.issues && c.issue_tags.length > 0}
                <div class="tag-row">
                  {#each c.issue_tags as tag}<span class="tag">{tag}</span>{/each}
                </div>
              {/if}

              {#if visible.issues && c.issue_other}
                <p class="entry-issue-other">Other: {c.issue_other}</p>
              {/if}

              <dl class="extras">
                {#if visible.yard_sign}
                  <div><dt>Yard sign</dt><dd>{fmtBool(c.yard_sign_requested)}</dd></div>
                {/if}
                {#if visible.volunteer_interest}
                  <div><dt>Vol. interest</dt><dd>{fmtBool(c.volunteer_interest)}</dd></div>
                {/if}
                {#if visible.donation_interest}
                  <div><dt>Donation interest</dt><dd>{fmtBool(c.donation_interest)}</dd></div>
                {/if}
                {#if visible.callback_at && c.callback_at}
                  <div><dt>Callback at</dt><dd>{fmtDate(c.callback_at)}</dd></div>
                {/if}
              </dl>
            </li>
          {/each}
        </ul>
      {/if}
    </div>
  </div>
</div>

<style>
  .overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,.5);
    display: flex; align-items: center; justify-content: center;
    z-index: 1100;
  }
  .sheet {
    background: white; border-radius: 12px;
    width: min(560px, 95vw); max-height: 85vh;
    display: flex; flex-direction: column;
    box-shadow: 0 8px 32px rgba(0,0,0,.25);
  }
  .header {
    display: flex; align-items: center; justify-content: space-between;
    gap: 8px; padding: 14px 16px; border-bottom: 1px solid #e5e7eb;
  }
  .header h2 { margin: 0; font-size: 1.05rem; }
  .header-actions { display: flex; gap: 8px; align-items: center; }
  .picker-toggle {
    background: #eee; border: none; padding: 6px 12px; border-radius: 8px;
    cursor: pointer; font-size: 0.85rem; min-height: 36px;
  }
  .picker-toggle:hover { background: #ddd; }
  .close-btn {
    background: none; border: none; font-size: 1.2rem; cursor: pointer;
    padding: 4px 10px; min-width: 36px; min-height: 36px;
  }
  .picker {
    margin: 0; padding: 12px 16px; border: none; border-bottom: 1px solid #e5e7eb;
    display: grid; grid-template-columns: repeat(2, 1fr); gap: 6px 16px;
    background: #f9fafb;
  }
  .picker legend { font-size: 0.8rem; font-weight: 600; color: #374151; padding: 0 4px; }
  .picker-row {
    display: flex; align-items: center; gap: 6px;
    font-size: 0.875rem; cursor: pointer;
  }
  .body {
    flex: 1; overflow-y: auto; padding: 8px 16px 16px;
  }
  .delete-btn {
    background: transparent; border: 1px solid #e5e7eb; color: #6b7280;
    border-radius: 6px; padding: 2px 6px; font-size: 0.75rem;
    cursor: pointer; line-height: 1; margin-left: auto;
  }
  .delete-btn:hover:not(:disabled) { background: #fee2e2; color: #991b1b; border-color: #fca5a5; }
  .delete-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .muted { color: #6b7280; font-size: 0.9rem; margin: 12px 0; }
  .error { color: #dc2626; }
  .history-list { list-style: none; padding: 0; margin: 0; }
  .entry {
    padding: 12px 0; border-bottom: 1px solid #f3f4f6;
  }
  .entry:last-child { border-bottom: none; }
  .entry-head {
    display: flex; flex-wrap: wrap; gap: 6px 10px; align-items: center;
    font-size: 0.9rem;
  }
  .entry-date { color: #374151; font-weight: 600; }
  .entry-vol { color: #1a73e8; font-size: 0.85rem; }
  .badge {
    font-size: 0.75rem; padding: 2px 8px; border-radius: 999px;
    background: #eee; color: #333;
  }
  .outcome-badge { background: #8e6bb8; color: #fff; }
  .result-spoke { background: #dcfce7; color: #166534; }
  .result-not_home { background: #fef3c7; color: #92400e; }
  .result-refused, .result-wrong_address { background: #fee2e2; color: #991b1b; }
  .result-moved { background: #e0e7ff; color: #3730a3; }
  .support-1, .support-2 { background: #fee2e2; color: #991b1b; }
  .support-3 { background: #fef3c7; color: #92400e; }
  .support-4, .support-5 { background: #dcfce7; color: #166534; }
  .entry-note {
    margin: 6px 0 0; font-size: 0.9rem; color: #374151;
    white-space: pre-wrap; word-break: break-word;
  }
  .tag-row { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 6px; }
  .tag {
    font-size: 0.75rem; background: #eef2ff; color: #3730a3;
    padding: 2px 8px; border-radius: 999px;
  }
  .entry-issue-other {
    margin: 6px 0 0; font-size: 0.85rem; color: #374151;
    word-break: break-word;
  }
  .extras {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 4px 12px; margin: 6px 0 0; font-size: 0.85rem;
  }
  .extras > div { display: flex; gap: 6px; }
  .extras dt { color: #6b7280; }
  .extras dd { margin: 0; color: #111827; }
</style>
